import React, { useMemo, useState, useEffect } from 'react';
import { format, addDays, isToday, isTomorrow, parseISO } from 'date-fns';
import { Calendar, Clock, AlertCircle, TrendingUp, CheckCircle, AlertTriangle, Zap } from 'lucide-react';
import AdaptiveTimelineSupport from '../College/AdaptiveTimelineSupport';
import CrisisSupport from '../Intelligence/CrisisSupport';

/**
 * TimelineSidebar
 *
 * Sidebar component showing week at a glance, upcoming deadlines, and progress
 * with crisis recovery support
 */
const TimelineSidebar = ({ timelineItems = [], workflows = [], studentId, currentUser }) => {
  const [showCrisisSupport, setShowCrisisSupport] = useState(false);
  const [crisisLevel, setCrisisLevel] = useState('normal');

  // Get tasks for next 7 days
  const weekTasks = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextWeek = addDays(today, 7);

    const filtered = timelineItems
      .filter(task => {
        if (!task.scheduled_date) return false;
        const taskDate = parseISO(task.scheduled_date);
        const isInRange = taskDate >= today && taskDate <= nextWeek;
        const isNotCompleted = task.completion_status !== 'completed' && !task.completed && task.status !== 'completed';
        return isInRange && isNotCompleted;
      })
      .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
      .slice(0, 10); // Show max 10 tasks

    // Debug: Log first few tasks
    console.log('[TimelineSidebar] Week tasks (first 5):', filtered.slice(0, 5).map(t => ({
      id: t.id,
      title: t.title,
      scheduled_date: t.scheduled_date,
      completion_status: t.completion_status,
      completed: t.completed,
      status: t.status
    })));

    return filtered;
  }, [timelineItems]);

  // Get upcoming deadlines (high priority or hard deadlines)
  const upcomingDeadlines = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const twoWeeks = addDays(today, 14);

    return timelineItems
      .filter(task => {
        if (!task.due_date && !task.scheduled_date) return false;
        if (task.completion_status === 'completed') return false;

        const deadline = parseISO(task.due_date || task.scheduled_date);
        return (
          (task.priority === 'critical' || task.is_hard_deadline) &&
          deadline >= today &&
          deadline <= twoWeeks
        );
      })
      .sort((a, b) => {
        const dateA = new Date(a.due_date || a.scheduled_date);
        const dateB = new Date(b.due_date || b.scheduled_date);
        return dateA - dateB;
      })
      .slice(0, 5);
  }, [timelineItems]);

  // Calculate workflow progress stats
  const progressStats = useMemo(() => {
    const totalWorkflows = workflows.length;
    const completedWorkflows = workflows.filter(w => w.completionPercentage === 100).length;
    const inProgressWorkflows = workflows.filter(w => w.completionPercentage > 0 && w.completionPercentage < 100).length;

    const totalSteps = workflows.reduce((sum, w) => sum + w.totalSteps, 0);
    const completedSteps = workflows.reduce((sum, w) => sum + w.completedSteps, 0);
    const overallProgress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

    return {
      totalWorkflows,
      completedWorkflows,
      inProgressWorkflows,
      totalSteps,
      completedSteps,
      overallProgress
    };
  }, [workflows]);

  // Calculate hours this week
  const weekHours = useMemo(() => {
    return weekTasks.reduce((sum, task) => {
      const hours = task.estimated_duration ? task.estimated_duration / 60 : 0.5;
      return sum + hours;
    }, 0);
  }, [weekTasks]);

  const getDateLabel = (dateString) => {
    const date = parseISO(dateString);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEE, MMM d');
  };

  // Detect crisis level based on overdue/urgent tasks
  useEffect(() => {
    if (!timelineItems.length) {
      setCrisisLevel('normal');
      return;
    }

    const now = new Date();
    const overdueTasks = timelineItems.filter(task => {
      if (task.completion_status === 'completed') return false;
      if (!task.scheduled_date && !task.due_date) return false;
      const taskDate = parseISO(task.scheduled_date || task.due_date);
      return taskDate < now;
    });

    const urgentTasks = timelineItems.filter(task => {
      if (task.completion_status === 'completed') return false;
      if (!task.scheduled_date && !task.due_date) return false;
      const taskDate = parseISO(task.scheduled_date || task.due_date);
      const daysUntil = Math.ceil((taskDate - now) / (1000 * 60 * 60 * 24));
      return daysUntil <= 3 && daysUntil >= 0 && (task.priority === 'critical' || task.is_hard_deadline);
    });

    if (overdueTasks.length >= 5) {
      setCrisisLevel('high');
    } else if (overdueTasks.length >= 3 || urgentTasks.length >= 4) {
      setCrisisLevel('moderate');
    } else if (overdueTasks.length > 0) {
      setCrisisLevel('low');
    } else {
      setCrisisLevel('normal');
    }
  }, [timelineItems]);

  // Handle crisis recovery button click
  const handleCrisisClick = () => {
    setShowCrisisSupport(true);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'clamp(1rem, 3vw, 1.5rem)'
    }}>
      {/* Week at a Glance */}
      <div style={{
        background: 'white',
        border: '2px solid #e7e5e4',
        borderRadius: '12px',
        padding: 'clamp(1rem, 3vw, 1.5rem)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: 'clamp(0.75rem, 2.5vw, 1rem)'
        }}>
          <Calendar style={{
            width: 'clamp(14px, 3vw, 16px)',
            height: 'clamp(14px, 3vw, 16px)',
            color: '#64748b'
          }} />
          <h3 style={{
            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
            fontWeight: 600,
            color: '#0f172a',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            margin: 0
          }}>
            This Week
          </h3>
        </div>

        <div style={{ marginBottom: 'clamp(0.75rem, 2.5vw, 1rem)' }}>
          <div style={{
            fontSize: 'clamp(1.5rem, 5vw, 2rem)',
            fontWeight: 700,
            color: '#0f172a',
            lineHeight: 1
          }}>
            {weekTasks.length}
          </div>
          <div style={{
            color: '#64748b',
            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
            marginTop: '0.25rem'
          }}>
            tasks • ~{Math.round(weekHours * 10) / 10}h
          </div>
        </div>

        {weekTasks.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {weekTasks.slice(0, 5).map(task => (
              <div
                key={task.id}
                style={{
                  paddingBottom: 'clamp(0.5rem, 2vw, 0.75rem)',
                  borderBottom: '1px solid #f5f5f4'
                }}
              >
                <div style={{
                  fontSize: 'clamp(0.75rem, 2vw, 0.8125rem)',
                  fontWeight: 500,
                  color: '#0f172a',
                  marginBottom: '0.25rem',
                  lineHeight: 1.4
                }}>
                  {task.title}
                </div>
                <div style={{
                  fontSize: 'clamp(0.6875rem, 1.8vw, 0.75rem)',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>{getDateLabel(task.scheduled_date)}</span>
                  <span>•</span>
                  <span>{Math.round((task.estimated_duration || 30) / 60 * 10) / 10}h</span>
                </div>
              </div>
            ))}
            {weekTasks.length > 5 && (
              <div style={{
                fontSize: 'clamp(0.6875rem, 1.8vw, 0.75rem)',
                color: '#64748b',
                textAlign: 'center',
                paddingTop: '0.5rem'
              }}>
                +{weekTasks.length - 5} more tasks
              </div>
            )}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '1.5rem 0',
            color: '#64748b',
            fontSize: '0.875rem'
          }}>
            No tasks scheduled this week
          </div>
        )}
      </div>

      {/* Upcoming Deadlines */}
      {upcomingDeadlines.length > 0 && (
        <div style={{
          background: 'white',
          border: '2px solid #fca5a5',
          borderRadius: '12px',
          padding: 'clamp(1rem, 3vw, 1.5rem)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem'
          }}>
            <AlertCircle className="w-4 h-4" style={{ color: '#dc2626' }} />
            <h3 style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#dc2626',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: 0
            }}>
              Critical Deadlines
            </h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {upcomingDeadlines.map(task => {
              const deadline = parseISO(task.due_date || task.scheduled_date);
              const daysUntil = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));

              return (
                <div
                  key={task.id}
                  style={{
                    paddingBottom: '0.75rem',
                    borderBottom: '1px solid #fee2e2'
                  }}
                >
                  <div style={{
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: '#0f172a',
                    marginBottom: '0.25rem',
                    lineHeight: 1.4
                  }}>
                    {task.title}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: daysUntil <= 3 ? '#dc2626' : '#64748b',
                    fontWeight: daysUntil <= 3 ? 600 : 400
                  }}>
                    {daysUntil === 0 ? 'Due today!' :
                     daysUntil === 1 ? 'Due tomorrow' :
                     `${daysUntil} days until ${format(deadline, 'MMM d')}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Milestone Progress */}
      <div style={{
        background: 'white',
        border: '2px solid #e7e5e4',
        borderRadius: '12px',
        padding: 'clamp(1rem, 3vw, 1.5rem)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <TrendingUp className="w-4 h-4" style={{ color: '#64748b' }} />
          <h3 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#0f172a',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            margin: 0
          }}>
            Progress
          </h3>
        </div>

        {/* Overall Progress Bar */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: '0.5rem'
          }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#0f172a' }}>
              Overall
            </span>
            <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
              {progressStats.overallProgress}%
            </span>
          </div>
          <div style={{
            height: '8px',
            background: '#f5f5f4',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progressStats.overallProgress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #26a69a 0%, #1a6b65 100%)',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{
            fontSize: '0.75rem',
            color: '#64748b',
            marginTop: '0.5rem'
          }}>
            {progressStats.completedSteps} of {progressStats.totalSteps} steps complete
          </div>
        </div>

        {/* Workflow Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.75rem'
        }}>
          <div style={{
            background: '#f5f5f4',
            borderRadius: '8px',
            padding: '1rem',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#0f172a',
              lineHeight: 1
            }}>
              {progressStats.inProgressWorkflows}
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: '#64748b',
              marginTop: '0.25rem'
            }}>
              In Progress
            </div>
          </div>

          <div style={{
            background: '#f5f5f4',
            borderRadius: '8px',
            padding: '1rem',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#26a69a',
              lineHeight: 1
            }}>
              {progressStats.completedWorkflows}
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: '#64748b',
              marginTop: '0.25rem'
            }}>
              Completed
            </div>
          </div>
        </div>
      </div>

      {/* Crisis Support Modal */}
      {showCrisisSupport && (
        <CrisisSupport
          tasks={timelineItems}
          userContext={{
            userId: studentId,
            studentName: currentUser?.name || 'Student'
          }}
          onPlanExecuted={() => {
            setShowCrisisSupport(false);
            // Optionally refresh timeline here
          }}
          onClose={() => setShowCrisisSupport(false)}
          isVisible={showCrisisSupport}
        />
      )}
    </div>
  );
};

export default TimelineSidebar;
